/** Öffentliche Schnittstelle: Produktname und Buchungsseiten der Auftritte. */

export type Location = 'meet' | 'phone';

export interface Meta {
  productName: string;
  registrationOpen: boolean;
}

export interface PublicEventType {
  slug: string;
  title: string;
  description: string | null;
  durationMinutes: number;
  location: Location;
}

export interface PublicBrand {
  slug: string;
  name: string;
  brandColor: string | null;
  logoUrl: string | null;
  imprintUrl: string | null;
  privacyUrl: string | null;
  eventTypes: PublicEventType[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Zusatzangaben der Antwort, z. B. attemptsLeft beim E-Mail-Code. */
    readonly details: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

export async function request<T>(path: string, init: { method?: string; body?: unknown; admin?: boolean } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // Der Schutz-Header gegen CSRF – ein fremdes Formular kann ihn nicht setzen.
      ...(init.admin ? { 'X-Requested-With': 'fetch' } : {}),
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (!res.ok) {
    let code = String(res.status);
    let message = '';
    let details: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as { error?: string; message?: string } & Record<string, unknown>;
      code = body.error ?? code;
      message = body.message ?? '';
      details = body;
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, message || code, details);
  }
  return (await res.json()) as T;
}

export interface Availability {
  zone: string;
  days: { day: string; slots: string[] }[];
  /** Google antwortet gerade nicht: keine Zeiten, stattdessen Terminwunsch (Konzept 5.4). */
  unavailable?: boolean;
}

export interface RequestPayload {
  name: string;
  email: string;
  formToken: string;
  /** Honigtopf – bleibt bei Menschen leer. */
  website: string;
}

export interface BookPayload {
  startsAt: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  formToken: string;
  /** Honigtopf – bleibt bei Menschen leer. */
  website: string;
  /** „E-Mail-Adresse ändern“: die bisherige Reservierung wird freigegeben. */
  holdId?: string;
}

export type BookingStatus = 'pending' | 'confirmed' | 'declined' | 'expired' | 'cancelled';

export interface Booked {
  id: string;
  token: string;
  startsAt: string;
  endsAt: string;
  /** pending = Anfrage, die der Anbieter erst annimmt. */
  status: 'pending' | 'confirmed';
  created: boolean;
}

/** E-Mail-Code vor der Buchung: der Termin ist reserviert, die Buchung kommt mit dem Code aus der Mail. */
export interface HoldStarted {
  verification: 'code';
  holdId: string;
  expiresAt: string;
  email: string;
}

export type BookOutcome = Booked | HoldStarted;

export function isHold(outcome: BookOutcome): outcome is HoldStarted {
  return 'verification' in outcome;
}

export interface CustomerBooking {
  id: string;
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  cancelledBy: 'customer' | 'host' | null;
  name: string;
  meetUrl: string | null;
  eventType: { slug: string; title: string; durationMinutes: number; location: Location };
  brand: { slug: string; name: string; brandColor: string | null; logoUrl: string | null; imprintUrl: string | null; privacyUrl: string | null };
}

export const fetchMeta = () => request<Meta>('/api/meta');
export const fetchBooking = (token: string) => request<CustomerBooking>(`/api/booking/${encodeURIComponent(token)}`);
export const cancelBooking = (token: string) => request<CustomerBooking>(`/api/booking/${encodeURIComponent(token)}/cancel`, { method: 'POST' });
export const fetchBookingSlots = (token: string) => request<Availability>(`/api/booking/${encodeURIComponent(token)}/slots`);
export const rescheduleBooking = (token: string, startsAt: string) => request<CustomerBooking>(`/api/booking/${encodeURIComponent(token)}/reschedule`, { method: 'POST', body: { startsAt } });
export const fetchBrand = (slug: string) => request<PublicBrand>(`/api/b/${encodeURIComponent(slug)}`);
export const fetchSlots = (brand: string, event: string) => request<Availability>(`/api/b/${encodeURIComponent(brand)}/${encodeURIComponent(event)}/slots`);
export const fetchFormToken = () => request<{ token: string }>('/api/form-token');
export const requestSlot = (brand: string, event: string, payload: RequestPayload) => request<{ ok: true; created: boolean }>(`/api/b/${encodeURIComponent(brand)}/${encodeURIComponent(event)}/request`, { method: 'POST', body: payload });
export const book = (brand: string, event: string, payload: BookPayload) => request<BookOutcome>(`/api/b/${encodeURIComponent(brand)}/${encodeURIComponent(event)}/bookings`, { method: 'POST', body: payload });
export const confirmCode = (brand: string, event: string, holdId: string, code: string) => request<Booked>(`/api/b/${encodeURIComponent(brand)}/${encodeURIComponent(event)}/bookings/confirm`, { method: 'POST', body: { holdId, code } });
export const resendCode = (brand: string, event: string, holdId: string) => request<{ ok: true; expiresAt: string; resendsLeft: number }>(`/api/b/${encodeURIComponent(brand)}/${encodeURIComponent(event)}/bookings/resend`, { method: 'POST', body: { holdId } });

export function errorText(error: unknown, fallback = 'Das hat nicht geklappt. Bitte versuch es noch einmal.'): string {
  if (error instanceof ApiError && error.code && !/^\d+$/.test(error.code) && /\s/.test(error.code)) return error.code;
  return fallback;
}

export function locationLabel(location: Location): string {
  return location === 'meet' ? 'Google Meet' : 'Telefon';
}
